import React, { useState } from "react";
import { Box } from "theme-ui";
import BasicModal from "components/ui/BasicModal";
import BasicButton from "components/forms/BasicButton";
import SelectInput from "components/forms/SelectInput";
import TextInput from "components/forms/TextInput";
import { QuestionMarkIcon, TrashIcon } from "components/svg";

const QUOTE_CATEGORIES = [
  { value: "lost_to_competitor", label: "Went with another vendor" },
  { value: "price_too_high", label: "Price wasn't competitive" },
  { value: "budget_cut", label: "Budget was reduced or eliminated" },
  { value: "project_cancelled", label: "Project was cancelled" },
  { value: "timeline_pushed", label: "Project delayed indefinitely" },
  { value: "scope_changed", label: "Requirements changed" },
  { value: "other", label: "Other" },
];

const RFQ_CATEGORIES = [
  { value: "just_budgeting", label: "Only needed a budget number" },
  { value: "project_not_awarded", label: "Didn't win the underlying project" },
  { value: "went_different_direction", label: "Chose a different approach" },
  { value: "project_cancelled", label: "Project was cancelled" },
  { value: "timeline_pushed", label: "Project delayed indefinitely" },
  { value: "other", label: "Other" },
];

interface DeclineReasonModalProps {
  type: "quote" | "rfq";
  onConfirm: (data: { category: string | null; notes?: string; competitorName?: string }) => void;
  onCancel: () => void;
  submitting?: boolean;
  title?: string;
}

const DeclineReasonModal = ({ type, onConfirm, onCancel, submitting = false, title }: DeclineReasonModalProps) => {
  const [category, setCategory] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [competitorName, setCompetitorName] = useState<string>("");

  const categories = type === "quote" ? QUOTE_CATEGORIES : RFQ_CATEGORIES;

  const handleConfirm = () => {
    onConfirm({
      category: category || null,
      notes: notes.trim() || undefined,
      competitorName: category === "lost_to_competitor" ? (competitorName.trim() || undefined) : undefined,
    });
  };

  return (
    <BasicModal
      title={title || (type === "quote" ? "Decline Quote" : "Cancel RFQ")}
      cancel_click={onCancel}
      cancel_text="Cancel"
      $submitting={submitting}
      size="sm"
      icon={<QuestionMarkIcon fill="#818181" height="30px" />}
      actions={
        <BasicButton
          type="button"
          scheme="danger"
          onClick={handleConfirm}
          $submitting={submitting}
          $iconHeight="14px"
          $iconWidth="14px"
          $loadingColor="#fff"
          keepText={true}
          styles={{ margin: "0 0.625rem .25rem 0" }}
        >
          {!submitting && <TrashIcon fill="#fff" />}
          {title ? "Confirm" : (type === "quote" ? "Decline Quote" : "Cancel RFQ")}
        </BasicButton>
      }
    >
      <Box sx={{ textAlign: "left", mt: 3 }}>
        <Box sx={{ fontFamily: "Poppins", fontSize: "12px", color: "#818181", mb: 3, textAlign: "center" }}>
          Select a reason to help us improve our service.
        </Box>
        <Box sx={{ mb: 3 }}>
          <SelectInput
            label="Reason"
            options={[{ value: "", label: "Select a reason (optional)" }, ...categories]}
            value={category}
            onChange={(val: any) => setCategory(val || "")}
            clearable={true}
          />
        </Box>

        {category === "lost_to_competitor" && (
          <Box sx={{ mb: 3 }}>
            <TextInput
              label="Competitor Name"
              value={competitorName}
              onChange={(val: any) => setCompetitorName(val || "")}
              placeholder="Who did you go with?"
            />
          </Box>
        )}

        <Box>
          <TextInput
            label="Additional Notes"
            type="textarea"
            value={notes}
            onChange={(val: any) => setNotes(val || "")}
            placeholder="Any additional feedback (optional)"
            rows={3}
          />
        </Box>
      </Box>
    </BasicModal>
  );
};

export default DeclineReasonModal;
